import { apiFetch } from 'services/apiClient';
import { Page } from '../../../types/Page';
import { InspectionAppointment } from '../types/InspectionAppointment';

export type InspectionParameterDeviceReadiness = {
  attributeId: number;
  attributeName: string;
  availableInstruments: Array<{ id: number; name: string }>;
  missingInstruments: Array<{ id: number; name: string }>;
};

export type InspectionDeviceReadiness = {
  appointmentId: number;
  proposalId: number | null;
  proposalNumber: string | null;
  companyId: number | null;
  companyName: string | null;
  inspectorId: number | null;
  inspectorName: string;
  startDate: string;
  endDate: string;
  availableInstruments: Array<{ id: number; name: string }>;
  missingInstruments: Array<{ id: number; name: string }>;
  status: 'COMPLETE' | 'MISSING' | 'NOT_REQUIRED';
  parameters: InspectionParameterDeviceReadiness[];
};

export const getInspectionDeviceReadiness = (
  startDate: string,
  endDate: string,
): Promise<InspectionDeviceReadiness[]> => {
  const params = new URLSearchParams({ startDate, endDate });
  return apiFetch(`/inspection-appointments/device-readiness?${params.toString()}`);
};

export const getMyInspectionDeviceReadiness = (
  startDate: string,
  endDate: string,
): Promise<InspectionDeviceReadiness[]> => {
  const params = new URLSearchParams({ startDate, endDate });
  return apiFetch(`/inspection-appointments/me/device-readiness?${params.toString()}`);
};

export const listInspectionAppointments = (page = 0, size = 10, sort?: string, query?: string): Promise<Page<InspectionAppointment>> =>
  apiFetch('/inspection-appointments?page=' + page + '&size=' + size + (sort ? '&sort=' + sort : '') + (query ? '&' + query : ''));
export const listInspectionAppointmentsAll = (query?: string): Promise<InspectionAppointment[]> =>
  apiFetch('/inspection-appointments/all' + (query ? `?${query}` : ''));
export const listMyInspectionAppointmentsAll = (query?: string): Promise<InspectionAppointment[]> =>
  apiFetch('/inspection-appointments/me/all' + (query ? `?${query}` : ''));
export const getInspectionAppointment = (id: number): Promise<InspectionAppointment> => apiFetch('/inspection-appointments/' + id);
export const createInspectionAppointment = (data: any): Promise<InspectionAppointment> =>
  apiFetch('/inspection-appointments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const updateInspectionAppointment = (id: number, data: any): Promise<InspectionAppointment> =>
  apiFetch('/inspection-appointments/' + id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const deleteInspectionAppointment = (id: number): Promise<void> =>
  apiFetch('/inspection-appointments/' + id, { method: 'DELETE' });
export const planInspectionAppointments = (data: any): Promise<any> =>
  apiFetch('/inspection-appointments/plan', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const getInspectionPlanByProposal = (proposalId: number): Promise<any> =>
  apiFetch('/inspection-appointments/plan/' + proposalId);

export const getInspectorAvailabilitySettings = (): Promise<{ mode: 'DAILY' | 'HOURLY' }> =>
  apiFetch('/inspection-appointments/availability/settings');

export const checkInspectorAvailability = (
  accountId: number,
  start: string,
  end: string,
  ignoreAppointmentId?: number,
): Promise<{ accountId: number; available: boolean; mode: 'DAILY' | 'HOURLY' }> => {
  const params = new URLSearchParams({
    accountId: String(accountId),
    start,
    end,
  });
  if (ignoreAppointmentId != null) {
    params.set('ignoreAppointmentId', String(ignoreAppointmentId));
  }
  return apiFetch(`/inspection-appointments/availability/check?${params.toString()}`);
};

export const fetchAvailableInspectorsForAttributes = (data: {
  attributeIds?: number[] | null;
  start: string;
  end: string;
  includeAccountIds?: number[] | null;
}): Promise<{ availableAccountIds: number[]; mode: 'DAILY' | 'HOURLY' }> =>
  apiFetch('/inspection-appointments/availability/inspectors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export type InspectorAvailabilitySlot = {
  start: string;
  end: string;
};

export const fetchInspectorAvailabilitySlots = (data: {
  accountId: number;
  attributeId: number;
  startDate: string;
  endDate: string;
  durationMinutes?: number;
}): Promise<{ eligible: boolean; slots: InspectorAvailabilitySlot[]; mode: 'DAILY' | 'HOURLY' }> => {
  const params = new URLSearchParams({
    accountId: String(data.accountId),
    attributeId: String(data.attributeId),
    startDate: data.startDate,
    endDate: data.endDate,
    durationMinutes: String(data.durationMinutes ?? 60),
  });
  return apiFetch(`/inspection-appointments/availability/slots?${params.toString()}`);
};

export const bulkUpdateAppointmentsByProposalAndAccount = (data: {
  proposalId: number;
  accountId: number;
  contractUrl?: string | null;
  contractId?: string | null;
  isCompanyProcessDone?: boolean | null;
  isInspectorProcessDone?: boolean | null;
  notifyContractUpload?: boolean | null;
  resetContract?: boolean | null;
  notifyContractCancellation?: boolean | null;
}): Promise<number> =>
  apiFetch('/inspection-appointments/bulk/update-by-proposal-and-account', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const updateInspectionAppointmentLegalProcess = (
  appointmentId: number,
  data: {
    contractUrl?: string | null;
    contractId?: string | null;
    isCompanyProcessDone?: boolean | null;
    isInspectorProcessDone?: boolean | null;
    resetContract?: boolean | null;
  },
): Promise<InspectionAppointment> =>
  apiFetch(`/inspection-appointments/${appointmentId}/legal-process`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export type PlanningInspectorContextResponse = {
  inspectors: any[];
  appointments: any[];
  leaves: any[];
  reservations: any[];
};

export const getPlanningInspectorContext = (
  proposalId: number,
  startDate: string,
  endDate: string,
): Promise<PlanningInspectorContextResponse> => {
  const params = new URLSearchParams({
    proposalId: String(proposalId),
    startDate,
    endDate,
  });
  return apiFetch('/inspection-appointments/planning/context?' + params.toString());
};
